import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Package, ArrowLeft, MapPin, Warehouse, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const StockAuditDelhi = () => {
  const handleCall = () => window.location.href = 'tel:9205514887';
  const handleWhatsApp = () => window.open('https://wa.me/919205514887', '_blank');

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Stock Audit Services in Delhi | Inventory Verification – AudiX</title>
        <meta name="description" content="Professional stock audit and inventory verification services in Delhi NCR. AudiX provides warehouse audits, retail audits, and inventory management across Delhi." />
        <link rel="canonical" href="https://www.audix.co.in/stock-audit-services-delhi" />
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-md py-4 px-4 sticky top-0 z-50">
        <div className="container mx-auto max-w-6xl flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Button onClick={handleCall} size="sm" className="bg-blue-600 hover:bg-blue-700">
            <Phone className="h-4 w-4 mr-2" /> Call Us
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Stock Audit Services in Delhi</h1>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto mb-8">
            Professional stock audit and inventory verification services for warehouses, retail chains, and distribution centers across Delhi NCR.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={handleCall} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Phone className="h-5 w-5 mr-2" /> Get Quote
            </Button>
            <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Stock Audit Services We Offer</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <Package className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Inventory Verification</h3>
                <p className="text-slate-600">Complete physical verification of inventory with detailed variance analysis and reconciliation.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Warehouse className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Warehouse Audits</h3>
                <p className="text-slate-600">Comprehensive audits covering stock movement, storage practices, and loss prevention.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <ClipboardList className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Retail Stock Audits</h3>
                <p className="text-slate-600">Multi-location retail audits with focus on shrinkage control and inventory accuracy.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-slate-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Benefits of Our Stock Audit Services</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Accurate inventory valuation and control',
              'Detection of shrinkage and pilferage',
              'Improved stock management processes',
              'Compliance with accounting standards',
              'Detailed variance analysis reports',
              'Experienced on-ground audit teams'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <p className="text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Related Services</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/audit-firm-in-delhi" className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-200">
              Audit Firm in Delhi
            </Link>
            <Link to="/internal-audit-services-delhi" className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-200">
              Internal Audit Services
            </Link>
            <Link to="/fixed-asset-verification-delhi" className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-200">
              Fixed Asset Verification
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-blue-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4">Need Stock Audit Services in Delhi?</h2>
          <p className="text-xl text-blue-100 mb-8">Contact us for professional inventory verification and stock audit services.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={handleCall} size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
              <Phone className="h-5 w-5 mr-2" /> 9205514887
            </Button>
            <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-8 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-slate-400">© 2025 AudiX Solutions & Co. All rights reserved.</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">← Back to Homepage</Link>
        </div>
      </footer>
    </div>
  );
};

export default StockAuditDelhi;
